<template>
  <transition>
    <div class="evi-modal position-fixed bg-white" :class="{ 'evi-modal--embedded': isEmbedded }">
      <div class="chatbot-wrapper">
        <section
          class="chatbot-header d-flex justify-content-center align-items-center text-center"
        >
          <div class="d-flex justify-content-center">
            <div class="d-flex align-items-center icon" v-html="AiMagicIcon" />
            <span>Evi</span>
          </div>
          <div class="close-icon position-absolute d-flex" @click="handleClose">
            <b-icon-x></b-icon-x>
          </div>
        </section>

        <div class="chatbot-content-wrapper d-flex">
          <div class="chat-content d-flex flex-column">
            <ChatBox
              v-if="account"
              :account="account"
              mode="external"
              :welcome-message="welcomeMessage(account?.name)"
              :default-questions="defaultQuestionOptions(account?.name)"
              @stream-completed="handleStreamCompleted"
            ></ChatBox>
          </div>

          <EviAssetPreviewer :account="account" :loading="loading" :assets="previewAssets" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import axios from 'axios'
import aiMagicIcon from '@images/icons/ai-magic.svg?raw'
import ChatBox from '@app/pages/content_assets/ChatBox.vue'
import EviAssetPreviewer from './EviAssetPreviewer.vue'

export default {
  components: { EviAssetPreviewer, ChatBox },
  data() {
    return {
      loadingPreviews: false,
      previewableAssets: [],
    }
  },
  props: {
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    account: {
      type: Object,
      required: true,
    },
  },
  computed: {
    AiMagicIcon() {
      return aiMagicIcon
    },
    previewAssets() {
      return this.previewableAssets
    },
    loading() {
      return this.loadingPreviews
    },
  },
  methods: {
    defaultQuestionOptions(companyName) {
      const questions = [
        `What is the ROI of implementing ${companyName}`,
        'Give me testimonials from companies in the tech sector',
        `What are the long-term benefits of using ${companyName}`,
      ]
      return questions
    },
    handleStreamCompleted(message) {
      this.findUeviLinks(message)
    },
    async findUeviLinks(message) {
      // This finds all the links in the chat response
      // containing https://uevi.co/
      const links = message.match(/https:\/\/uevi.co\/\w+/g)

      // Sometimes the LLM can return something in the following pattern:
      // (https://uevi.co/5f7)[https://uevi.co/5f7)
      // so we need to get only the uniqs from the array
      const uniqLinks = [...new Set(links)]

      if (uniqLinks && uniqLinks.length > 0) {
        this.previewableAssets = []
        this.loadingPreviews = true
        const ids = uniqLinks.map((link) => {
          const items = link.split('/')
          return items[items.length - 1]
        })

        axios
          .all(ids.map((id) => axios.get(`/api/content_assets/${id}.json`)))
          .then(
            axios.spread((...res) => {
              // all requests to fetch asset data are now complete
              res.forEach((r) => {
                this.previewableAssets.push(r.data)
              })
            }),
          )
          .finally(() => {
            this.loadingPreviews = false
          })
      }
    },
    handleClose() {
      this.previewableAssets = []
      this.$emit('evi-close')
    },
    welcomeMessage(company) {
      return `Hi 👋, I’m Evi. Ask me anything about ${company}’s user evidence. Here are a few examples of what you can ask:`
    },
  },
}
</script>

<style lang="sass" scoped>

.v-enter-active,
.v-leave-active
  transition: opacity 0.5s ease

.v-enter-from,
.v-leave-to
  opacity: 0

.evi-modal
  max-width: 1100px
  max-height: 700px
  z-index: 99
  width: 75%
  top: 30%
  left: 50%
  transform: translate(-50%, -30%)
  border-radius: 1rem
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1)

  &--embedded
    top: 15px
    left: 50%
    transform: translate(-50%, 15px)

.chatbot-wrapper
  border: 1px solid #e0e0e0
  border-radius: 1rem

.chatbot-content-wrapper
  border-top: 1px solid #e0e0e0

.chatbot-header
  padding: 0.5rem
  font-size: 1rem
  font-weight: var(--headings-font-weight)
  ::v-deep svg
    width: 30px
    height: 30px
  .close-icon
    cursor: pointer
    margin-left: 95%
    svg
      color: black

.chat-content
  width: 50%

@media screen and ( max-height: 768px )
  .evi-modal
    top: 30%
    left: 50%
    transform: translate(-50%, -30%)
    max-height: 650px
    ::v-deep .chat-box-content--external
      height: 485px
    ::v-deep .preview-content-wrapper
      height: 560px
    ::v-deep .bottom-controls--external
</style>
